'use client';

import Link from 'next/link';
import Image from 'next/image';
import {
  Anchor,
  Box,
  Button,
  Group,
  Stack,
  Switch,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { IconMoon, IconSun } from '@tabler/icons-react';
import { useMantineColorScheme } from '@mantine/core';
import { useStableColorScheme } from '../../../lib/use-stable-color-scheme';
import { InboxBell } from './inbox/InboxBell';

interface HeaderProps {
  showAuthButton?: boolean;
  showInbox?: boolean;
  withinAppShell?: boolean;
}

export default function Header({ showAuthButton = false, showInbox = false, withinAppShell = false }: HeaderProps) {
  const theme = useMantineTheme();
  const { setColorScheme } = useMantineColorScheme();
  const colorScheme = useStableColorScheme();
  const isDark = colorScheme === 'dark';
  const headerColor = isDark ? theme.other.headerColorDark : theme.other.headerColorLight;
  const textColor = isDark ? theme.other.textColorDark : theme.other.textColorLight;
  const lineColor = isDark ? theme.other.lineColorDark : theme.other.lineColorLight;

  return (
    <Box
      component="header"
      // explicit role для a11y: `<header>` внутри `<main>` (NAV-CONT-D-002 — Header
      // рендерится в AppShell.Main) теряет implicit role="banner" (ARIA in HTML:
      // banner-role только при direct-child-of-body). Восстанавливаем явно, чтобы
      // ARIA landmark оставался доступным — контракт §8 a11y / §13.3 AC #4.
      role="banner"
      bg={headerColor}
      style={{
        borderBottom: `1px solid ${lineColor}`,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        position: withinAppShell ? 'static' : 'sticky',
        top: withinAppShell ? undefined : 0,
        zIndex: withinAppShell ? undefined : 15,
        height: '100%',
        boxShadow: 'none',
      }}
    >
      <Group justify="space-between" wrap="nowrap" mih={theme.other.headerHeight} px={{ base: 'md', sm: 'xl' }} py="md">
        <Anchor component={Link} href="/" underline="never">
          <Group gap={14} wrap="nowrap">
            <Box
              w={theme.other.logoSize}
              h={theme.other.logoSize}
              pos="relative"
              style={{ transition: `opacity ${theme.other.transitionDuration} ease` }}
            >
              <Image
                src={isDark ? '/logo/logo_dark.png' : '/logo/logo_light.png'}
                alt="Синтра"
                fill
                sizes={theme.other.logoSize}
                priority
              />
            </Box>

            <Text
              size="21px"
              fw={800}
              c={textColor}
              style={{ fontFamily: 'var(--font-unbounded), sans-serif' }}
              lts={theme.other.titleLetterSpacing}
              ff={theme.other.accentFontFamily}
            >
              Синтра
            </Text>
          </Group>
        </Anchor>

        <Group gap="md" wrap="nowrap">
          {showAuthButton ? (
            <Button
              component={Link}
              href="/login"
              variant="default"
              size="md"
              px="lg"
            >
              🔑 Войти
            </Button>
          ) : null}

          {/* REQ-INB-001 (inbox): колокольчик слева от Switch темы. Виден только
              аутентифицированным (showInbox=true в ProtectedShell). Тема остаётся
              крайним правым элементом (концепция §14.1); aria-label темы не меняется. */}
          {showInbox ? <InboxBell /> : null}

          <Stack gap={0} align="center">
            <Switch
              checked={isDark}
              onChange={(event) => setColorScheme(event.currentTarget.checked ? 'dark' : 'light')}
              size="md"
              aria-label="Переключить тему"
              onLabel={<IconSun size={12} stroke={2.2} />}
              offLabel={<IconMoon size={12} stroke={2.2} />}
              // FLAT per-part (canonical ARCHCODE-001, parity с §4.7 / seminars П.7):
              // никаких вложенных селекторов — тернар по params.checked только.
              styles={(theme, params) => ({
                track: {
                  width: 44,
                  height: 26,
                  borderRadius: 999,
                  border: 'none',
                  transition: 'background 0.2s ease',
                  ...(params.checked
                    ? { backgroundImage: theme.other.brandGradient }
                    : { backgroundColor: lineColor }),
                },
                thumb: { width: 20, height: 20, backgroundColor: '#fff' },
              })}
            />
          </Stack>
        </Group>
      </Group>
    </Box>
  );
}
